import { loggerCan } from '@/data/utils/loggerUtil';
import { ContentType, HttpClient, RequestParams } from './http-client';
import { logger } from '@/logging/logger';
import {
	ClubdpCardForCheckOut,
	ClubdpCards,
	ClubdpCardUnlink,
	ClubdpFlags,
} from './data-contracts';

export class Clubdp<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;
	traceDetails: string | undefined;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
		this.traceDetails = process.env.TRACE_DETAILS?.trim();
	}

	/**
	 * @tags transactions
	 * @name clubdpCards
	 * @summary Get clubdp cards
	 * @request GET:/store/{storeId}/clubdp/loyalty/cards
	 * @secure
	 * @description Get clubdp cards
	 * @param {string} storeId - Store id
	 * @param {string} responseFormat - Response format
	 * @param {boolean} transactions - Include transactions
	 * @param {RequestParams} params - request parameters
	 * @return {ClubdpCards} Clubdp cards
	 * @return {void} Clubdp cards
	 * @return {any} Clubdp cards
	 */
	clubdpCards = (
		storeId: string,
		query?: {
			responseFormat?: 'xml' | 'json';
			transactions?: boolean;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (loggerCan('trace') && (!this.traceDetails || this.traceDetails.includes('clubdpCards'))) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: null ?? {},
				methodName: 'clubdpCards',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<ClubdpCards, void>({
			path: `/store/${storeId}/clubdp/loyalty/cards`,
			method: 'GET',
			query: {
				...query,
				transactions: 'true',
			},
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	/**
	 * @tags transactions
	 * @name clubdpFlags
	 * @summary Get clubdp flags
	 * @request GET:/store/{storeId}/clubdp/loyalty/flags
	 * @secure
	 * @description Get clubdp flags
	 * @param {string} storeId - Store id
	 * @param {string} responseFormat - Response format
	 * @param {boolean} transactions - Include transactions
	 * @param {RequestParams} params - request parameters
	 * @return {ClubdpFlags} Clubdp flags
	 * @return {void} Clubdp flags
	 * @return {any} Clubdp flags
	 */
	clubdpFlags = (
		storeId: string,
		query?: {
			responseFormat?: 'xml' | 'json';
			transactions?: boolean;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (loggerCan('trace') && (!this.traceDetails || this.traceDetails.includes('clubdpFlags'))) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: null ?? {},
				methodName: 'clubdpFlags',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<ClubdpFlags, void>({
			path: `/store/${storeId}/clubdp/loyalty/flags`,
			method: 'GET',
			query: query,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	/**
	 * @tags transactions
	 * @name clubdpCardUnlink
	 * @summary Unlink clubdp card
	 * @request POST:/store/{storeId}/clubdp/loyalty/card/unlink
	 * @secure
	 * @description Unlink clubdp card
	 * @param {string} storeId - Store id
	 * @param {string} responseFormat - Response format
	 * @param {ClubdpCardUnlink} data - Clubdp card unlink
	 * @param {RequestParams} params - request parameters
	 * @return {ClubdpCardUnlink} Clubdp card unlink
	 * @return {void} Clubdp card unlink
	 * @return {any} Clubdp card unlink
	 */
	clubdpCardUnlink = (
		storeId: string,
		query: {
			/** The response format. If the request has an input body, that body must also use the format specified in "responseFormat". Valid values include json and xml without the quotes. If the responseFormat isn't specified, the accept HTTP header shall be used to determine the format of the response. If the accept HTTP header isn't specified as well, the default response format shall be in json. */
			responseFormat?: string;
		},
		data: {
			password: string;
			email: string;
			cardType: number;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('clubdpCardUnlink'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: null ?? {},
				methodName: 'clubdpCardUnlink',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}

		return this.http.request<ClubdpCardUnlink, void>({
			path: `/store/${storeId}/clubdp/loyalty/card/unlink`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	/**
	 * @tags transactions
	 * @name clubdpCardValidate
	 * @summary Validate clubdp card
	 * @request GET:/store/{storeId}/clubdp/loyalty/card/validate/{cardId}
	 * @secure
	 * @description Validate clubdp card
	 * @param {string} storeId - Store id
	 * @param {string} cardId - Card id to validate
	 * @param {string} responseFormat - Response format
	 * @param {RequestParams} params - request parameters
	 * @return {any} Validation result
	 */
	clubdpCardValidate = (
		storeId: string,
		cardId: string,
		query?: {
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('clubdpCardValidate'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: null ?? {},
				methodName: 'clubdpCardValidate',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}

		return this.http.request<any, void>({
			path: `/store/${storeId}/clubdp/loyalty/card/validate/${cardId}`,
			method: 'GET',
			query: query,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	clubdpCardForCheckOut = (
		storeId: string,
		query?: {
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('clubdpCardForCheckOut'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: null ?? {},
				methodName: 'clubdpCardForCheckOut',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}

		return this.http.request<ClubdpCardForCheckOut, void>({
			path: `/store/${storeId}/clubdp/loyalty/card-for-checkout`,
			method: 'GET',
			query: query,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	clubdpLoyaltyOtpDistributor = (
		storeId: string,
		data: {
			phone: string;
			cardId: string;
			email: string;
			password: string;
		},
		query?: {
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('clubdpLoyaltyOtpDistributor'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: data ?? {},
				methodName: 'clubdpLoyaltyOtpDistributor',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<
			{
				sentTo: string;
				message: string;
				sent: boolean;
			},
			void
		>({
			path: `/store/${storeId}/clubdp/loyalty/otp-distributor`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	clubdpLoyaltyCardLink = (
		storeId: string,
		data: {
			password: string;
			email: string;
			phone: string;
			code: string;
			cardId: string;
		},
		query?: {
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('clubdpLoyaltyCardLink'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: data ?? {},
				methodName: 'clubdpLoyaltyCardLink',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<any, void>({
			path: `/store/${storeId}/clubdp/loyalty/card/link`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};
}
